'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@7f-ui/components/button/button';
import { Price } from '@7f-ui/components/price';
import { formatDateWithCompactWeekday } from '@utils/utils-format';
import type { PurchasableDay } from '../_hooks/use-app-experience-purchasable-days';
import { useAppTicketSelection, useOpeningHoursLabel } from '../_hooks/use-app-ticket-selection';
import { AppExperienceCancelation } from './app-experience-cancelation';
import { useExperienceFilterContext } from './app-experience-filter-provider';
type SubmitProps = {
  submitLabel: string;
  isSubmitting?: boolean;
  disabled?: boolean;
  onSubmit: () => void;
};
export function AppTicketSheetFooter(t0) {
  const $ = _c(18);
  const {
    submitLabel,
    isSubmitting,
    disabled,
    onSubmit
  } = t0;
  const {
    experience
  } = useExperienceFilterContext();
  const {
    total,
    totalCount
  } = useAppTicketSelection();
  let t1;
  if ($[0] !== experience.cancelationDeadline) {
    t1 = <AppExperienceCancelation cancelationHours={experience.cancelationDeadline} isTicket={true} />;
    $[0] = experience.cancelationDeadline;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== experience.currency || $[3] !== total) {
    t2 = <Price className="font-heading4" amount={total} currency={experience.currency} />;
    $[2] = experience.currency;
    $[3] = total;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="font-body-small text-secondary-text">gesamt inkl. MwSt.</span>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <div className="grid">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = totalCount === 0 || isSubmitting || disabled;
  let t6;
  if ($[8] !== onSubmit || $[9] !== submitLabel || $[10] !== t5) {
    t6 = <Button onClick={onSubmit} disabled={t5} data-testid="TicketSelection.addToCart">{submitLabel}</Button>;
    $[8] = onSubmit;
    $[9] = submitLabel;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div className="flex items-center justify-between gap-4">{t4}{t6}</div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t7) {
    t8 = <div className="grid gap-6">{t1}{t7}</div>;
    $[15] = t1;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
type AppTicketCalendarFooterProps = SubmitProps & {
  day: PurchasableDay | null;
};
export function AppTicketCalendarFooter(t0) {
  const $ = _c(25);
  const {
    day,
    submitLabel,
    isSubmitting,
    disabled,
    onSubmit
  } = t0;
  const {
    experience
  } = useExperienceFilterContext();
  const {
    total,
    totalCount,
    ticketsBreakdown
  } = useAppTicketSelection();
  const getOpeningHoursLabel = useOpeningHoursLabel();
  let t1;
  if ($[0] !== experience.cancelationDeadline) {
    t1 = <AppExperienceCancelation cancelationHours={experience.cancelationDeadline} isTicket={true} />;
    $[0] = experience.cancelationDeadline;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== day || $[3] !== getOpeningHoursLabel) {
    t2 = day && <span className="font-body-copy">{formatDateWithCompactWeekday(day.date)}, {getOpeningHoursLabel(day)}</span>;
    $[2] = day;
    $[3] = getOpeningHoursLabel;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== ticketsBreakdown) {
    t3 = <span className="font-body-copy">{ticketsBreakdown}</span>;
    $[5] = ticketsBreakdown;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="grid gap-1">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="font-body-small text-secondary-text">inkl. MwSt.</span>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== experience.currency || $[12] !== total) {
    t6 = <div className="grid justify-items-end">{t5}<Price className="font-heading4" amount={total} currency={experience.currency} /></div>;
    $[11] = experience.currency;
    $[12] = total;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <div className="flex items-start justify-between gap-4" data-testid="TicketSelection.summary">{t4}{t6}</div>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = totalCount === 0 || isSubmitting || disabled;
  let t9;
  if ($[17] !== onSubmit || $[18] !== submitLabel || $[19] !== t8) {
    t9 = <Button full={true} onClick={onSubmit} disabled={t8} data-testid="TicketSelection.addToCart">{submitLabel}</Button>;
    $[17] = onSubmit;
    $[18] = submitLabel;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t1 || $[22] !== t7 || $[23] !== t9) {
    t10 = <div className="grid gap-4">{t1}{t7}{t9}</div>;
    $[21] = t1;
    $[22] = t7;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
}